import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Grid,
  RadioGroup,
  Radio,
  Slider,
  useTheme,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import PaletteIcon from '@mui/icons-material/Palette';
import { getSettings, saveSettings } from '../utils/storage';
import { useThemeContext } from '../App';

const Settings = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState(getSettings());
  const { setIsDarkMode } = useThemeContext();
  const [patentSettings, setPatentSettings] = useState(settings.patentSettings || {
    verseDisplayLevel: 'verse', // 'verse', 'sentence', 'phrase'
    removalMethod: 'time',
    presentationTimeSeconds: 30,
    maxRepetitions: 3,
    realTimeEvaluation: true,
    detectSilence: true,
    silenceDetectionSeconds: 3,
    detectWordCount: true,
    responseMethod: 'highlight',
    progressCompilationType: 'progressive',
    accuracyThreshold: 80
  });

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' },
    { code: 'ru', name: 'Russian' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
  ];

  const handleLanguageChange = (event) => {
    const newSettings = { ...settings, language: event.target.value };
    setSettings(newSettings);
    saveSettings({ ...newSettings, patentSettings });
  };

  const handleThemeModeChange = (event) => {
    const newThemeMode = event.target.value;
    const newSettings = { ...settings, themeMode: newThemeMode };
    setSettings(newSettings);
    // Update global theme state
    if (newThemeMode === 'dark') {
      setIsDarkMode(true);
    } else if (newThemeMode === 'light') {
      setIsDarkMode(false);
    } else if (newThemeMode === 'auto') {
      setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    saveSettings({ ...newSettings, patentSettings });
  };

  const handlePatentSettingChange = (key, value) => {
    const newPatentSettings = { ...patentSettings, [key]: value };
    setPatentSettings(newPatentSettings);
    saveSettings({ ...settings, patentSettings: newPatentSettings });
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Card
        sx={{
          p: 4,
          mb: 4,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'surfaceContainerHighest',
          border: `1px solid ${theme.palette.outline.variant}`,
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SettingsIcon sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  mb: 1
                }}
              >
                Memorization Settings
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                Configure your Bible memory experience
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Settings Card */}
      <Card
        sx={{
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.outline.light}`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Language and Theme - Always Horizontal */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <Grid item xs sx={{ minWidth: 120 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Language:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={settings.language || 'en'}
                      onChange={handleLanguageChange}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                          py: 0.75,
                        }
                      }}
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              <Grid item xs sx={{ minWidth: 120 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Theme:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={settings.themeMode || 'light'}
                      onChange={handleThemeModeChange}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                          py: 0.75,
                        }
                      }}
                    >
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="dark">Dark</MenuItem>
                      <MenuItem value="auto">Auto</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: theme.palette.outline.light, mb: 4 }} />
          </Box>

          {/* Required Accuracy Level */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }}>
              Required Accuracy
            </Typography>
            <Divider sx={{ mb: 4, borderColor: theme.palette.outline.light }} />

            <Slider
              value={patentSettings.accuracyThreshold}
              onChange={(e, value) => handlePatentSettingChange('accuracyThreshold', value)}
              min={50}
              max={100}
              step={5}
              valueLabelDisplay="auto"
              marks={[
                { value: 50, label: '50%' },
                { value: 70, label: '70%' },
                { value: 80, label: '80%' },
                { value: 90, label: '90%' },
                { value: 95, label: '95%' },
                { value: 100, label: '100%' }
              ]}
              sx={{ mt: 2 }}
            />

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>Learning Goal:</strong> Set your practice accuracy requirement. Higher accuracy builds better retention, but learning is still effective at moderate levels. 100% accuracy is for final mastery.
              </Typography>
            </Alert>
          </Box>

          {/* Verse Display */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }}>
              Verse Display
            </Typography>
            <Divider sx={{ mb: 4, borderColor: theme.palette.outline.light }} />

            <FormControl component="fieldset" fullWidth>
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Choose your default display style:
              </Typography>
              <RadioGroup
                value={patentSettings.verseDisplayLevel}
                onChange={(e) => handlePatentSettingChange('verseDisplayLevel', e.target.value)}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="verse"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                        Full Verse
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Show the entire verse without splitting
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />

                <FormControlLabel
                  value="sentence"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                        By Sentence
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Split the verse by sentence endings (periods, question marks)
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />

                <FormControlLabel
                  value="phrase"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                        By Phrase
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Split by commas and colons to show natural thought units
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>

            <Alert severity="info" sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>Display Strategy:</strong>
                {patentSettings.verseDisplayLevel === 'verse' && " Show complete verses - traditional approach."}
                {patentSettings.verseDisplayLevel === 'sentence' && " Split verses by sentence endings - builds complete thoughts."}
                {patentSettings.verseDisplayLevel === 'phrase' && " Split by commas and colons - learns natural thought units."}
              </Typography>
            </Alert>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>On-Demand Adjustment:</strong> Use More/Less buttons on the memorize card to temporarily adjust display for individual verses without changing your default setting.
              </Typography>
            </Alert>
          </Box>

          {/* How Long to Display Before Hiding */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }}>
              How Long to Display Before Hiding
            </Typography>
            <Divider sx={{ mb: 4, borderColor: theme.palette.outline.light }} />

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={patentSettings.removalMethod}
                onChange={(e) => handlePatentSettingChange('removalMethod', e.target.value)}
              >
                <FormControlLabel
                  value="time"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Time-Based Memorization
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Show text for a set time, then hide it automatically
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 3 }}
                />

                <FormControlLabel
                  value="repetitions"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={(
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Repeat-Based Memorization
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Keep showing until recited multiple times correctly
                      </Typography>
                    </Box>
                  )}
                  sx={{ mb: 2 }}
                />
              </RadioGroup>
            </FormControl>

            <Alert severity="info" sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>Learning Method:</strong> Time-based memorization helps build quick recall skills. Repeat-based memorization focuses on accuracy for challenging content.
              </Typography>
            </Alert>
          </Box>

          {/* Speech Recognition & Feedback Features */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }}>
              Speech Recognition & Feedback Features
            </Typography>
            <Divider sx={{ mb: 4, borderColor: theme.palette.outline.light }} />

            <Grid container spacing={4}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={patentSettings.realTimeEvaluation}
                      onChange={(e) => handlePatentSettingChange('realTimeEvaluation', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Live Word-by-Word Checking
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Get instant feedback as you speak each word
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 3, alignItems: 'flex-start' }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={patentSettings.detectSilence}
                      onChange={(e) => handlePatentSettingChange('detectSilence', e.target.checked)}
                    />
                  }
                  label={(
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Smart Recording Pause
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Automatically stop recording when you finish speaking
                      </Typography>
                    </Box>
                  )}
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>Advanced Feature:</strong> Real-time speech recognition converts what you say into text and checks accuracy instantly. Smart pause detection makes recording feel natural.
              </Typography>
            </Alert>
          </Box>

          {/* Learn Gradually or Review Pieces */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }}>
              Learn Gradually or Review Pieces
            </Typography>
            <Divider sx={{ mb: 4, borderColor: theme.palette.outline.light }} />

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={patentSettings.progressCompilationType}
                onChange={(e) => handlePatentSettingChange('progressCompilationType', e.target.value)}
              >
                <FormControlLabel
                  value="isolated"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Master Each Piece First
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Practice one chunk until perfect, then move to the next
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 3, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  value="progressive"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Build Gradually
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Add new chunks to previously mastered content for connected learning
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>

            <Alert severity="info" sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>Learning Strategy:</strong> "Build Gradually" is more effective for memorizing verses as complete units. "Master Each Piece First" works well for difficult passages or when building confidence.
              </Typography>
            </Alert>
          </Box>

          {/* How to Handle Errors & Feedback */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 3 }}>
              How to Handle Errors & Feedback
            </Typography>
            <Divider sx={{ mb: 4, borderColor: theme.palette.outline.light }} />

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={patentSettings.responseMethod}
                onChange={(e) => handlePatentSettingChange('responseMethod', e.target.value)}
              >
                <FormControlLabel
                  value="you_said"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={(
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Show What You Said
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Display your spoken words for comparison
                      </Typography>
                    </Box>
                  )}
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  value="correct_response"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Show Correct Answer
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Display the correct text to learn from
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  value="highlight"
                  control={<Radio sx={{ color: theme.palette.primary.main }} />}
                  label={(
                    <Box>
                      <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                        Highlight Mistakes
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Color-code correct/incorrect words for easy identification
                      </Typography>
                    </Box>
                  )}
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>

            <Alert severity="info" sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                <strong>Learning Method:</strong> Choose how you'd like to receive feedback when you make mistakes during practice. Highlighting provides the clearest visual guidance.
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>


    </Box>
  );
};

export default Settings;
